import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { 
  AlertTriangle, 
  CheckCircle, 
  Info, 
  XCircle,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertCircle,
  Activity,
  Filter
} from 'lucide-react';

/**
 * AuditDashboard - Price Audit & Discrepancy Logging Dashboard
 * 
 * Features:
 * - Summary statistics (total logs, discrepancies by type/severity)
 * - Discrepancy list with filtering
 * - Booking audit trail viewer
 * - Integrity check trigger
 */
const AuditDashboard = ({ API }) => {
  // State
  const [summary, setSummary] = useState(null);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIntegrity, setCheckingIntegrity] = useState(false);
  const [integrityResults, setIntegrityResults] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [bookingAuditTrail, setBookingAuditTrail] = useState([]);
  const [loadingAuditTrail, setLoadingAuditTrail] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Fetch summary on mount
  const fetchSummary = useCallback(async () => {
    try {
      const response = await apiService.get('/admin/audit/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching audit summary:', error);
      toast.error('Failed to load audit summary');
    }
  }, []);

  // Fetch discrepancies
  const fetchDiscrepancies = useCallback(async () => {
    try {
      let queryParams = '?limit=200';
      if (severityFilter !== 'all') {
        queryParams += `&severity=${severityFilter}`;
      }
      const response = await apiService.get(`/admin/audit/discrepancies${queryParams}`);
      setDiscrepancies(response.data?.discrepancies || []);
    } catch (error) {
      console.error('Error fetching discrepancies:', error);
      toast.error('Failed to load discrepancies');
    }
  }, [severityFilter]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchDiscrepancies()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSummary, fetchDiscrepancies]);

  // Run integrity check
  const runIntegrityCheck = async () => {
    setCheckingIntegrity(true);
    try {
      const response = await apiService.post('/admin/audit/check-integrity');
      setIntegrityResults(response.data);
      toast.success(`Integrity check complete: ${response.data?.discrepancies_found || 0} issues found`);
      // Refresh data after check
      await Promise.all([fetchSummary(), fetchDiscrepancies()]);
    } catch (error) {
      console.error('Error running integrity check:', error);
      toast.error('Failed to run integrity check');
    } finally {
      setCheckingIntegrity(false);
    }
  };

  // Fetch audit trail for specific booking
  const fetchBookingAuditTrail = async () => {
    if (!selectedBookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    
    setLoadingAuditTrail(true);
    try {
      const response = await apiService.get(`/admin/audit/booking/${selectedBookingId}`);
      setBookingAuditTrail(response.data?.audit_trail || []);
      if (response.data?.audit_trail?.length === 0) {
        toast.info('No audit logs found for this booking');
      }
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      toast.error('Failed to load audit trail');
    } finally {
      setLoadingAuditTrail(false);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  // Severity badge component
  const SeverityBadge = ({ severity }) => {
    const config = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
      error: { bg: 'bg-red-50', text: 'text-red-700', icon: AlertTriangle },
      warning: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: AlertCircle },
      info: { bg: 'bg-blue-50', text: 'text-blue-700', icon: Info }
    };
    const { bg, text, icon: Icon } = config[severity] || config.info;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
        <Icon className="w-3 h-3" />
        {severity?.toUpperCase()}
      </span>
    );
  };

  // Event type badge
  const EventTypeBadge = ({ type }) => {
    const config = {
      price_discrepancy: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Price' },
      payment_discrepancy: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Payment' },
      status_discrepancy: { bg: 'bg-red-100', text: 'text-red-800', label: 'Status' },
      payment_completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
      payment_failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
      booking_created: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Created' },
      beds24_sync: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Beds24' },
      manual_correction: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Correction' }
    };
    const { bg, text, label } = config[type] || { bg: 'bg-gray-100', text: 'text-gray-600', label: type };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${bg} ${text}`}>
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading audit data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="audit-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Price audit, payment tracking, and discrepancy detection</p>
        </div>
        <button
          onClick={runIntegrityCheck}
          disabled={checkingIntegrity}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="run-integrity-check-btn"
        >
          <RefreshCw className={`w-4 h-4 ${checkingIntegrity ? 'animate-spin' : ''}`} />
          {checkingIntegrity ? 'Checking...' : 'Run Integrity Check'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Logs */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Audit Logs</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.total_logs || 0}</p>
            </div>
          </div>
        </div>

        {/* Total Discrepancies */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Discrepancies</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.total_discrepancies || 0}</p>
            </div>
          </div>
        </div>

        {/* Payment Issues */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Activity className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Payment Discrepancies</p>
              <p className="text-2xl font-bold text-gray-900">
                {summary?.events_by_type?.payment_discrepancy || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Placeholder for 4th card - keeps grid balanced */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Payment Events</p>
              <p className="text-2xl font-bold text-gray-900">
                {(summary?.events_by_type?.payment_completed || 0) + (summary?.events_by_type?.payment_failed || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Severity Breakdown */}
      {summary?.discrepancies_by_severity && Object.keys(summary.discrepancies_by_severity).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Discrepancies by Severity</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(summary.discrepancies_by_severity).map(([severity, count]) => (
              <div key={severity} className="flex items-center gap-2">
                <SeverityBadge severity={severity} />
                <span className="text-lg font-semibold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Integrity Check Results */}
      {integrityResults && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <h3 className="font-medium text-gray-900">Latest Integrity Check Results</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Bookings Checked:</span>
              <span className="ml-2 font-medium">{integrityResults.checked}</span>
            </div>
            <div>
              <span className="text-gray-500">Issues Found:</span>
              <span className="ml-2 font-medium text-red-600">{integrityResults.discrepancies_found}</span>
            </div>
          </div>
        </div>
      )}

      {/* Booking Audit Trail Search */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <h3 className="font-medium text-gray-900 mb-3">Search Booking Audit Trail</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={selectedBookingId}
            onChange={(e) => setSelectedBookingId(e.target.value)}
            placeholder="Enter Booking ID (e.g., BK123456)"
            className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="booking-id-input"
          />
          <button
            onClick={fetchBookingAuditTrail}
            disabled={loadingAuditTrail}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
            data-testid="search-audit-trail-btn"
          >
            <Search className="w-4 h-4" />
            {loadingAuditTrail ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Audit Trail Results */}
        {bookingAuditTrail.length > 0 && (
          <div className="mt-4 border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Triggered By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookingAuditTrail.map((log, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <EventTypeBadge type={log.event_type} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{log.triggered_by}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {log.discrepancy ? (
                        <span className="text-red-600">
                          {log.discrepancy.type}: Expected {JSON.stringify(log.discrepancy.expected)}, 
                          Got {JSON.stringify(log.discrepancy.actual)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Discrepancies List */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h3 className="font-medium text-gray-900">All Discrepancies</h3>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="severity-filter"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expected</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {discrepancies.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    No discrepancies found
                  </td>
                </tr>
              ) : (
                discrepancies.map((log, index) => (
                  <React.Fragment key={index}>
                    <tr 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleRowExpansion(index)}
                    >
                      <td className="px-4 py-3 text-sm font-mono text-blue-600">
                        {log.booking_id}
                      </td>
                      <td className="px-4 py-3">
                        <EventTypeBadge type={log.event_type} />
                      </td>
                      <td className="px-4 py-3">
                        <SeverityBadge severity={log.discrepancy?.severity} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {typeof log.discrepancy?.expected === 'number' 
                          ? `€${log.discrepancy.expected.toFixed(2)}`
                          : log.discrepancy?.expected}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {typeof log.discrepancy?.actual === 'number'
                          ? `€${log.discrepancy.actual.toFixed(2)}`
                          : log.discrepancy?.actual}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(log.timestamp).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        {expandedRows.has(index) ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </td>
                    </tr>
                    {expandedRows.has(index) && (
                      <tr className="bg-gray-50">
                        <td colSpan="7" className="px-4 py-3">
                          <div className="text-sm space-y-2">
                            <div>
                              <span className="text-gray-500">Description:</span>
                              <span className="ml-2 text-gray-700">{log.details?.description || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Triggered by:</span>
                              <span className="ml-2 text-gray-700">{log.triggered_by}</span>
                            </div>
                            {log.discrepancy?.difference !== undefined && (
                              <div>
                                <span className="text-gray-500">Difference:</span>
                                <span className={`ml-2 font-medium ${log.discrepancy.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {log.discrepancy.difference > 0 ? '+' : ''}€{log.discrepancy.difference.toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditDashboard;
